// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-A.yaml
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480, LOGISPRD-13852, LOGISPRD-13871, LOGISPRD-14470, LOGISPRD-14571, LOGISPRD-14591, LOGISPRD-14614, LOGISPRD-14923
import React, { useEffect, useMemo, useRef } from 'react'
import { FilterArea } from '@musinsa/pbo-core'
import type { FilterFieldSpec } from '@musinsa/pbo-core'
import { Form } from 'antd'
import MAlert from '@/utils/modal/MAlert'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { buildShipperNameOptions, useShipperNameFilterOptions, type ShipperOptionSource } from '@/components/system/shipperSelectFilter'
import { FILTER_FIELDS, MAX_SKU_ID_FILTER_SIZE, MAX_SKU_ID_LENGTH, SERVICE_TYPE_OPTIONS } from './consts'
import { withParticle } from './particle'
import { SkuAttributeParams } from '../queries/useGetSkuAttributeList'
import { toFilterFormValues } from './fromQuery'
import { type ShipperScope, canSelectShipper, defaultShipper, scopedShipperCode, withCoMingle, lockParams, scopeShippers, shipperScopeOf } from './shipperScope'

/**
 * 화주 없이 조회하면 MWP·MLT 전 화주 상품이 통째로 실린다 — 화면의 별표대로 여기서 막는다.
 * **SKU ID 를 넣었으면 예외다** (LOGISPRD-13852) — 결과가 입력한 SKU 로 한정되고, 사용자가 그 SKU 의
 * 화주를 모를 수 있다.
 */
export const SHIPPER_REQUIRED_MESSAGE = '화주(유형 또는 명) 또는 SKU ID 를 입력해 주세요.'

export const SKU_ID_LIMIT_MESSAGE = `SKU ID 는 한 번에 최대 ${MAX_SKU_ID_FILTER_SIZE.toLocaleString()}건까지 조회할 수 있습니다.`

/**
 * SKU ID 조건 검사 — BE 와 같은 기준이다. 통과하면 `null`.
 *
 * 개수는 **실제로 보내는 목록**([asSkuIds] — 빈 값·중복을 걷어낸 뒤)으로 센다. BE 는 받은 목록의 개수로
 * 거절하는데 화면이 걷어낸 목록을 보내므로 둘이 같은 숫자를 본다. 붙여 넣기의 중복 때문에 막히지 않는다.
 *
 * 안내에 싣는 숫자도 그 걷어낸 건수라 **「중복 제외」라고 밝힌다** — 200줄을 붙여 넣은 사용자에게
 * 「입력 101건」이라고 하면 자기가 넣은 숫자와 달라 헷갈린다 (LOGISPRD-13871).
 */
export const skuIdFilterError = (values: Record<string, unknown>): string | null => {
  const ids = asSkuIds(values['f-skuId']) ?? []
  if (ids.length > MAX_SKU_ID_FILTER_SIZE) return `${SKU_ID_LIMIT_MESSAGE} (중복 제외 ${ids.length.toLocaleString()}건)`
  if (ids.some((v) => v.length > MAX_SKU_ID_LENGTH)) return `SKU ID 는 ${MAX_SKU_ID_LENGTH}자를 넘을 수 없습니다.`
  return null
}

interface Props {
  coMingle?: boolean
  scope?: ShipperScope
  /**
   * 화주 **원본 목록**이다 — 가공된 옵션이 아니다.
   *
   * 옵션 만들기를 화면이 하면 (a) 선택된 유형으로 좁히는 일과 (b) 동명 화주에 코드를 덧붙이는
   * 일을 각자 다시 구현하게 된다. 그 둘을 한 곳에서 하는 공용 헬퍼가 있으므로 원본을 받아
   * 그쪽에 넘긴다 ([useShipperNameFilterOptions]).
   */
  shippers: ShipperOptionSource[]
  /** 화주 목록 조회가 끝났는가 (성공·실패 무관). 자동조회와 `FilterArea` 재마운트 시점이 여기에 걸린다 */
  shippersReady: boolean
  initialParams?: SkuAttributeParams
  onSearch: (params: SkuAttributeParams) => void
  /**
   * 진입 자동조회. [조회] 버튼(`onSearch`)과 나눈 것은 **진입은 이전 결과를 되살릴 수 있어서**다 — 다른 메뉴 탭에
   * 다녀오면 화면이 다시 마운트되는데, 같은 조건이면 다시 받지 않고 보던 목록을 보여 준다 (LOGISPRD-14923).
   * 주지 않으면 `onSearch` 로 간다
   */
  onInitialSearch?: (params: SkuAttributeParams) => void
  onReset: () => void
  /**
   * 필터의 화주명 선택이 바뀔 때마다 부른다(조회 전 포함) — 표가 조회 전에도 그 화주로 [추가]·[엑셀 업로드]를 열 수
   * 있게 한다 (LOGISPRD-14591). 화면이 하나로 줄이므로(keepSingleShipper, LOGISPRD-14614) 항상 단건이고, 비었으면 `undefined`
   */
  onShipperSelect?: (shipperCode: string | undefined) => void
}

/**
 * 다건 입력 칸의 폼 값에서 **한 값만** 꺼낸다.
 *
 * `mfs.FormInput` 은 `plain: true` 가 없으면 폼 값이 `string[]` 이다. BE 가 단일 값 파라미터라
 * 하나만 보낼 수 있는데, **둘 이상을 조용히 버리면 사용자는 나머지가 빠진 것을 모른다** —
 * 화주명 다중선택은 이미 같은 이유로 막아 두었다. 그래서 여기서는 값만 꺼내고,
 * 「둘 이상인가」 판정은 [tooManyValues] 가 따로 한다.
 */
const first = (v: unknown): string | undefined => {
  if (Array.isArray(v)) return v.length ? String(v[0]).trim() || undefined : undefined
  return typeof v === 'string' && v.trim() ? v.trim() : undefined
}

const asList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) return v.length ? v.map(String) : undefined
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

/**
 * SKU ID 다건 칸의 값 — trim 하고 빈 값·중복을 걷어낸다. 남는 것이 없으면 `undefined`.
 *
 * 붙여 넣기는 빈 줄·중복이 흔하다. 걷어낸 목록을 보내 요청을 작게 하고, 개수 상한도 이 목록으로
 * 판정한다 ([skuIdFilterError]).
 */
const asSkuIds = (v: unknown): string[] | undefined => {
  const ids = [...new Set((asList(v) ?? []).map((s) => s.trim()).filter(Boolean))]
  return ids.length ? ids : undefined
}

/**
 * 다건 입력 칸의 라벨 — 경고 문구에 쓴다.
 *
 * `FILTER_FIELDS` 에서 파생하지 않고 **여기 다시 적는다.** `FilterFieldSpec.label` 이
 * `string` 이라는 보장이 타입에 없어(ReactNode 를 받을 수 있다) 파생하면 런타임 타입 검사가
 * 들어가야 하고, 그 검사가 조용히 빗나가면 **경고가 안 뜨는 칸이 생긴다** — 이 표의 목적이
 * 「조용히 버리지 않는 것」이라 그 실패 모드는 목적과 정면으로 어긋난다.
 *
 * > **대신 id 가 갈리면 경고가 사라진다.** `consts.ts` 의 `FILTER_FIELDS` 에서 이 여섯 칸의 `id`
 * > 를 바꾸면 여기도 함께 고쳐야 한다. 아래 테스트가 두 선언의 id 집합이 같은지 잠근다.
 *
 * **SKU ID(`f-skuId`)는 이 표에 없다** — 유일한 다건 조건이라 둘 이상이 정상이다 (LOGISPRD-13852).
 * 상품단위는 필터에서 뺐다.
 */
const MULTI_VALUE_FIELD_LABELS: Record<string, string> = {
  'f-barcode': '바코드',
  'f-itemCode': '상품코드',
  'f-itemName': '상품명',
  'f-brandName': '브랜드명',
  'f-color': '색상',
  'f-styleNumber': '스타일번호',
}

/**
 * 화주명 선택을 **하나로 줄인다** — 둘 이상이면 마지막(방금 고른) 화주만 남긴 목록을, 이미 하나 이하면 `null` (LOGISPRD-14614).
 *
 * pbo-core 화주명 셀렉트는 다중 선택이고 `FilterArea` 가 단일 선택 옵션(`nameMode`)을 넘기지 않는다. 그런데 이 화면의 조회·
 * 등록·엑셀 업로드는 화주 하나 단위라, 여러 화주가 골라지면 어느 화주로 등록할지 모호하다. antd 다중 셀렉트는 새로 고른 값을
 * 목록 끝에 붙이므로 마지막 값을 남기면 「다른 화주를 고르면 그 화주로 바뀌는」 단일 선택처럼 동작한다.
 */
export const keepSingleShipper = (value: unknown): string[] | null => {
  const list = asList(value)
  if (!list || list.length <= 1) return null
  return [list[list.length - 1]]
}

/** 값이 둘 이상 들어간 칸의 라벨. 비어 있으면 전부 단건이다. */
export const tooManyValues = (values: Record<string, unknown>): string[] =>
  Object.entries(MULTI_VALUE_FIELD_LABELS)
    .filter(([key]) => (asList(values[key])?.length ?? 0) > 1)
    .map(([, label]) => label)

/** 테스트가 `FILTER_FIELDS` 와 대조할 수 있게 열어 둔다 (id 가 갈리면 경고가 사라진다). */
export const MULTI_VALUE_FIELD_IDS = Object.keys(MULTI_VALUE_FIELD_LABELS)

const hasShipperFilter = (params: SkuAttributeParams): boolean =>
  (params.serviceTypes?.length ?? 0) > 0 || !!params.shipperCode

/** 조회를 내보낼 수 있는가 — 화주 축 또는 SKU ID 중 하나가 있어야 한다 (SHIPPER_REQUIRED_MESSAGE KDoc). */
export const hasRequiredFilter = (params: SkuAttributeParams): boolean =>
  hasShipperFilter(params) || (params.skuIds?.length ?? 0) > 0

/**
 * 첫 진입 자동조회 조건. `null` 이면 **조회하지 않는다**.
 *
 * - 복원 조건이 화주 없이 **SKU ID 만** 있으면 화주 목록을 기다리지 않는다 — 그 조회에는 화주가 필요 없다.
 *   기다리면 화주 API 가 실패했을 때 SKU ID 로 연 URL 이 영영 조회되지 않는다 (LOGISPRD-13871)
 * - 그 밖에는 화주 목록 조회가 끝날 때까지 기다린다 — 화주 축 복원 조건은 목록이 와야 폼(화주명 옵션)과 맞물린다
 * - 복원 조건이 없으면 기본 화주로 조회한다. **기본 화주가 없으면(통합 운영 계정) 조회하지 않는다**
 *   (LOGISPRD-14470) — 사용자가 화주를 고르고 [조회]를 누른다
 */
export const initialSearchParams = (
  initialParams: SkuAttributeParams | undefined,
  baseShipperCode: string | undefined,
  shippersReady: boolean,
): SkuAttributeParams | null => {
  if (initialParams && !hasShipperFilter(initialParams) && hasRequiredFilter(initialParams)) return initialParams
  if (!shippersReady) return null
  // 복원 조건은 화주 범위로 덮인 뒤라 다시 본다 — 필수 조건 없이 나가면 [조회]의 필수 검사를 우회한다
  if (initialParams) return hasRequiredFilter(initialParams) ? initialParams : null
  return baseShipperCode ? { shipperCode: baseShipperCode } : null
}

/**
 * 화주명은 **단건만 보낸다.**
 *
 * BE `shipperCode` 가 단일 값 파라미터이고, 여러 화주를 한 번에 보려면 `serviceTypes` 축을 쓴다.
 * 여러 개를 고르면 첫 값만 쓰는 것은 조용한 누락이라, 화면에서 **단일 선택으로 막는다** — pbo-core `ShipperSelect` 는
 * 다중 선택이지만 화면이 선택을 하나로 줄인다(keepSingleShipper, LOGISPRD-14614). 여기는 첫 값을 꺼내기만 한다.
 *
 * 테스트가 폼 값 → 조회 조건 매핑을 잠글 수 있게 `export` 한다 ([tooManyValues] 와 같은 이유).
 */
export const toParams = (values: Record<string, unknown>): SkuAttributeParams => ({
  serviceTypes: asList(values['logisticsServiceTypes']),
  shipperCode: first(values['shipperNames']),
  barcode: first(values['f-barcode']),
  itemCode: first(values['f-itemCode']),
  itemName: first(values['f-itemName']),
  brandName: first(values['f-brandName']),
  color: first(values['f-color']),
  styleNumber: first(values['f-styleNumber']),
  skuIds: asSkuIds(values['f-skuId']),
})

/**
 * pbo-core `logistics.ShipperSelect` 가 화주 칸 컨테이너에 다는 `data-node-id` — 화주유형 필드 이름과 같다.
 * 화주 고정 계정의 입력 차단과 화면 CSS 가 이 값으로 화주 칸을 찾는다.
 */
export const SHIPPER_FIELD_NODE_ID = 'logisticsServiceTypes'

/**
 * 화주 고정 계정에서 화주 칸으로 가는 마우스·포커스·키 입력을 **바깥에서 가로챈다** (LOGISPRD-14571).
 *
 * pbo-core `FilterArea` 는 화주 셀렉트에 `disabled` 를 넘길 통로가 없다(`renderField` 가 넘기는 prop 에 없음).
 * 그래서 감싼 요소의 캡처 단계에서 이벤트를 끊는다 — 셀렉트는 포커스만으로도 드롭다운을 열고, 열리면 Backspace 로
 * 선택을 지울 수 있어 마우스만 막아서는(`pointer-events`) 키보드로 뚫린다. 화면이 보여 주는 값과 조회 조건은
 * 이것과 별개로 `lockParams` 가 자기 화주로 덮는다 — 이 차단은 「바꿀 수 있어 보이는」 화면을 없애는 몫이다.
 */
export const blockLockedShipperInput = (event: React.SyntheticEvent, tabBackward = false): void => {
  const target = event.target as HTMLElement | null
  if (!target?.closest?.(`[data-node-id="${SHIPPER_FIELD_NODE_ID}"]`)) return
  event.preventDefault()
  // 마우스는 기본 동작만 막고 전파는 살린다 — 끊으면 window 의 바깥 클릭 감지가 못 받아 열려 있던 다건 입력
  // 팝오버·다른 셀렉트 드롭다운이 닫히지 않는다. 셀렉트를 여는 마우스 입력은 CSS `pointer-events: none` 이 막는다
  if (event.type === 'mousedown' || event.type === 'click') return
  // focus·keydown 은 전파를 끊어야 셀렉트의 onFocus(드롭다운 열기)·Backspace(선택 지우기)가 돌지 않는다.
  // focus 의 preventDefault 는 효과가 없어 실제 포커스는 옆 칸으로 넘긴다
  event.stopPropagation()
  if (event.type === 'focus') moveFocusPastLockedShipper(target, tabBackward)
}

/** Tab 순서에 드는 요소 — 잠긴 화주 칸을 건너뛸 때 다음/이전 칸을 찾는다 */
const TABBABLE =
  'input:not([disabled]):not([type="hidden"]), button:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

/**
 * 잠긴 화주 칸에 들어온 포커스를 **Tab 방향의 옆 칸으로 넘긴다** (LOGISPRD-14571, PR #944 리뷰).
 *
 * `blur()` 로 떼어내기만 하면 포커스가 `document.body` 로 돌아가 다음 Tab 이 문서 처음부터 시작한다 — 키보드로
 * 필터를 훑던 사용자가 길을 잃는다. 그래서 Shift+Tab 으로 들어왔으면 앞 칸, 아니면 뒤 칸에 포커스를 준다.
 * 넘길 곳이 없을 때만 떼어낸다.
 */
export const moveFocusPastLockedShipper = (target: HTMLElement, backward: boolean): void => {
  const field = target.closest(`[data-node-id="${SHIPPER_FIELD_NODE_ID}"]`)
  if (!field) return
  // 문서 순서대로 늘어놓고 화주 칸의 위치를 기준으로 앞·뒤를 고른다 (querySelectorAll 은 문서 순서를 지킨다)
  const ordered = [...document.querySelectorAll<HTMLElement>(TABBABLE)]
  const firstInField = ordered.findIndex((el) => field.contains(el))
  if (firstInField < 0) {
    target.blur()
    return
  }
  const outside = (el: HTMLElement) => !field.contains(el)
  const neighbor = backward ? ordered.slice(0, firstInField).filter(outside).pop() : ordered.slice(firstInField).find(outside)
  if (neighbor) neighbor.focus()
  else target.blur()
}

export const SkuAttributeFilter = ({
  coMingle = false,
  scope: providedScope,
  shippers: allShippers,
  shippersReady,
  initialParams: restoredParams,
  onSearch,
  onInitialSearch = onSearch,
  onReset,
  onShipperSelect,
}: Props) => {
  // 폼을 **이 컴포넌트가 소유한다.** `useShipperNameFilterOptions` 가 폼을 직접 읽고 써야 하기
  // 때문이다 — 유형이 바뀌었을 때 남은 화주명 선택을 걷어내는 일은 옵션 계산만으로는 못 한다
  const [form] = Form.useForm()

  /**
   * 화주 범위 (LOGISPRD-14470) — 통합 운영(MUSINSA) 상태만 자유롭게 고르고, 화주 계정은 자기 화주로
   * 고정한다. 옵션·기본값·초기화·URL 복원·조회 조건이 모두 이 범위를 지난다. 화주 전환은 reload 를
   * 동반해 새 마운트를 만들므로 마운트 시점 한 번만 읽는다
   */
  const scope = useMemo(
    () => providedScope ?? withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingle),
    [providedScope, coMingle],
  )
  const shipperLocked = !canSelectShipper(scope)
  const coMingleSelection = scope.locked && !shipperLocked
  const shippers = useMemo(() => scopeShippers(allShippers, scope), [allShippers, scope])
  const initialParams = useMemo(() => restoredParams && lockParams(restoredParams, scope), [restoredParams, scope])

  /**
   * 화주명 옵션 + **유형이 바뀌면 어긋난 선택을 비운다** (LOGISPRD-12609, GR02·RGR04 와 같은 배선).
   *
   * ### 이 훅을 쓰지 않으면 남의 화주로 채번된다
   *
   * pbo-core `logistics.ShipperSelect` 는 유형이 바뀔 때 화주명 선택을 비우지 않는다. 비우는 것이
   * 화면 몫이라 이 훅이 만들어졌다. 초판은 같은 파일에서 `SHIPPER_REQUIRED_MARK_ONLY` 만 가져오고
   * 이 훅을 쓰지 않아, 유형을 MLT 로 바꿔도 화주명에 MWP 화주가 남았다. `toQuery` 는
   * `shipperCode` 가 있으면 `serviceTypes` 를 버리므로 **화면엔 MLT, 조회는 MWP** 가 됐고,
   * 그 상태에서 [추가]·[엑셀 업로드]한 행이 **화면에 보이지도 않는 화주로 채번**됐다.
   *
   * `valueKey: 'shipperCode'` 인 것은 BE `shipperCode` 가 단일 값 파라미터이기 때문이다.
   * 그 덕에 라벨은 `29CM_NIKESAMPLE (MWP-60)` 형태가 되어 **동명 화주 4개도 갈린다** —
   * 화주명만 띄우면 같은 글자 4줄 중 하나를 감으로 고르게 된다.
   */
  const filteredShipperNameOptions = useShipperNameFilterOptions({
    form,
    typeFieldName: 'logisticsServiceTypes',
    nameFieldName: 'shipperNames',
    shippers,
    valueKey: 'shipperCode',
    resetKey: `${coMingle}-${shippersReady}`,
  })

  const shipperNameOptions = coMingleSelection ? buildShipperNameOptions(shippers, { valueKey: 'shipperCode' }) : filteredShipperNameOptions

  /**
   * 기본 화주 — 화주 계정은 [defaultShipper](재고통합 대상 계정은 MUSINSA, LOGISPRD-15719), 통합 운영 계정은 **없다** (LOGISPRD-14470).
   *
   * 초판은 세션 화주가 MWP·MLT 목록에 없으면 **첫 화주로 폴백**했다. 그래서 통합 운영 계정은 늘
   * `29CMOFFLINE` 이 미리 골라진 채 열렸고, 전환 화주를 `SPR_NM` 으로 찾던 탓에 GLOWNY 로 전환한
   * 계정도 같은 화주로 떨어졌다. 고르지 않은 화주를 대신 고르지 않는다.
   */
  const baseShipperCode = scope.locked ? defaultShipper(scope).shipperCode || undefined : undefined

  /** 잠긴 계정의 화주유형 옵션은 선택 가능한 화주들의 유형이다. */
  const typeOptions = useMemo(() => {
    if (!scope.locked) return SERVICE_TYPE_OPTIONS
    return [...new Set(shippers.map((s) => s.logisticsServiceType).filter(Boolean))].map((type) => ({ label: type, value: type }))
  }, [scope, shippers])

  /**
   * 화주 계정의 고정 화주 칸 값 — 화주명뿐 아니라 **화주유형도 자기 유형으로 채운다** (LOGISPRD-14571).
   * 유형 칸을 비워 두면 잠긴 칸이 「선택 안 함」으로 보인다. 조회에는 쓰이지 않는다(`lockParams` 가 유형을 뺀다)
   */
  // 인라인 폼 핸들러에서만 사용하므로 함수 참조를 고정할 필요가 없다.
  const scopedShipperValues = (shipperCode?: string) => {
    if (!scope.locked) return undefined
    const code = scopedShipperCode(scope, shipperCode)
    const type = shippers.find((s) => s.shipperCode === code)?.logisticsServiceType
    return { shipperNames: [code], logisticsServiceTypes: type ? [type] : undefined }
  }

  const fields: FilterFieldSpec[] = useMemo(
    () => FILTER_FIELDS.map((f) => (f.id === 'f-shipper' ? { ...f, typeOptions, nameOptions: shipperNameOptions } : f)),
    [typeOptions, shipperNameOptions],
  )

  /**
   * 화주명 선택을 표에 알린다 — 조회 전에도 등록 대상이 된다 (LOGISPRD-14591).
   *
   * `preserve: true` 가 핵심이다 — pbo-core 는 필터를 접으면 화주 칸을 언마운트하는데, 그때 기본 `useWatch` 는 마운트된
   * 필드만 읽어 `undefined` 를 낸다. 그러면 접은 채 [엑셀 업로드] 를 누르면 다시 「화주명을 선택한 뒤 …」로 막힌다.
   * 둘 이상이 잡힌 렌더(아래 effect 가 하나로 줄이기 직전)에는 **남을 화주(방금 고른 것)** 를 미리 알린다 — 비어 있다고
   * 한 번 알렸다가 다시 알리면 표가 등록 대상을 잠깐 잃는다 (LOGISPRD-14614)
   */
  const watchedShipperNames: unknown = Form.useWatch('shipperNames', { form, preserve: true })
  const selectedShipperCode = keepSingleShipper(watchedShipperNames)?.[0] ?? first(watchedShipperNames)
  useEffect(() => {
    onShipperSelect?.(selectedShipperCode)
  }, [onShipperSelect, selectedShipperCode])

  // MUSINSA 선택 시 유형도 선택한 화주의 유형으로 맞춘다.
  useEffect(() => {
    if (!coMingleSelection) return
    // 재마운트 직후 useWatch는 이전 값을 줄 수 있어 현재 폼에서 선택값을 읽는다.
    const current = first(form.getFieldValue('shipperNames'))
    if (!current) return
    const type = shippers.find((s) => s.shipperCode === current)?.logisticsServiceType
    const types = type ? [type] : undefined
    if (first(form.getFieldValue('logisticsServiceTypes')) !== type) {
      form.setFieldValue('logisticsServiceTypes', types)
    }
  }, [coMingleSelection, selectedShipperCode, shippers, form])

  // 화주명은 하나만 고른다 — 다른 화주를 고르면 기존 선택이 그 화주로 바뀐다 (LOGISPRD-14614, keepSingleShipper KDoc)
  useEffect(() => {
    const single = keepSingleShipper(watchedShipperNames)
    if (single) form.setFieldValue('shipperNames', single)
  }, [form, watchedShipperNames])

  // 초기 진입 1회 — 복원 조건이 있으면 그 값으로, 없으면 화주 계정만 자기 화주로 자동 조회한다
  // (통합 운영 계정은 기본 화주가 없어 조회하지 않는다 — LOGISPRD-14470).
  // 언제 나가는지는 initialSearchParams 가 정한다 (SKU ID 단독 복원은 화주 목록을 기다리지 않는다)
  const didInit = useRef(false)

  /** 마지막 Tab 이 Shift+Tab 이었나 — 잠긴 화주 칸에 들어온 포커스를 어느 쪽으로 넘길지 정한다 */
  const tabBackward = useRef(false)
  useEffect(() => {
    const params = initialSearchParams(initialParams, baseShipperCode, shippersReady)
    if (didInit.current || !params) return
    didInit.current = true
    onInitialSearch(params)
  }, [baseShipperCode, shippersReady, onInitialSearch, initialParams])

  return (
    // 화주를 선택할 수 없는 계정만 화주 칸을 잠근다. 잠금 표시는
    // skuAttributeManagement.css 가, 입력 차단은 [blockLockedShipperInput] 이 한다
    <div
      className={shipperLocked ? 'sku-attr-filter sku-attr-filter--shipper-locked' : 'sku-attr-filter'}
      onMouseDownCapture={shipperLocked ? blockLockedShipperInput : undefined}
      onClickCapture={shipperLocked ? blockLockedShipperInput : undefined}
      onFocusCapture={shipperLocked ? (event) => blockLockedShipperInput(event, tabBackward.current) : undefined}
      onKeyDownCapture={
        shipperLocked
          ? (event) => {
              // Tab 은 포커스가 옮겨 가기 전(이전 칸)에서 눌리므로 여기서 방향만 기억해 둔다
              if (event.key === 'Tab') tabBackward.current = event.shiftKey
              blockLockedShipperInput(event)
            }
          : undefined
      }
    >
      <FilterArea
        // 화주 목록이 오면 한 번 재마운트해 폼 초기값(화주명)을 옵션과 맞물린다. 목록은 로딩 뒤 바뀌지 않으므로
        // 유형을 바꿔도 재마운트되지 않는다 — 재마운트되면 바코드 등 입력값이 통째로 사라진다
        key={shippersReady ? 'ready' : 'loading'}
        form={form}
        fields={fields}
        // **복원 조건 전체를 되돌린다.** 화주만 채우면 목록은 걸러졌는데 입력칸은 비어 있는 상태가
        // 되고, 그 상태로 [조회]를 다시 누르면 조건이 조용히 풀린다 (toFilterFormValues KDoc)
        onFormReady={(form) => {
          if (initialParams) form.setFieldsValue(toFilterFormValues(initialParams))
          else if (baseShipperCode) form.setFieldsValue({ shipperNames: [baseShipperCode] })
          const shipperValues = scopedShipperValues(initialParams?.shipperCode)
          if (shipperValues) form.setFieldsValue(shipperValues)
        }}
        onSearch={(values) => {
          // 허용 범위 밖의 값은 자기 화주로 되돌리고 선택한 화주의 유형을 표시한다.
          const shipperValues = scopedShipperValues(first(values['shipperNames']))
          if (shipperValues) form.setFieldsValue(shipperValues)
          const params = lockParams(toParams(values), scope)
          if (!hasRequiredFilter(params)) {
            MAlert.show(SHIPPER_REQUIRED_MESSAGE)
            return
          }
          // BE 도 400 으로 막지만 그 응답은 고정 문구라 무엇을 줄여야 하는지 알려 주지 못한다
          const skuIdError = skuIdFilterError(values)
          if (skuIdError) {
            MAlert.show(skuIdError)
            return
          }
          const picked = asList(values['shipperNames'])
          if ((picked?.length ?? 0) > 1) {
            // 조용히 첫 값만 쓰면 사용자는 나머지 화주가 빠진 것을 모른다. 화면이 하나만 남기므로(keepSingleShipper,
            // LOGISPRD-14614) 지금은 도달하는 경로가 없다 — 그 effect 를 거치지 않는 경로가 생길 때를 대비한 방어선이다
            MAlert.show('화주명은 하나만 선택할 수 있습니다. 여러 화주를 보려면 화주유형으로 조회하세요.')
            return
          }

          // **같은 이유로 나머지 여섯 칸도 막는다** (SKU ID 는 다건 조건이라 제외). 초판은 화주명만 막고 이 칸들은 첫 값만 쓰고
          // 나머지를 조용히 버렸다 — 같은 화면 안에서 기준이 갈렸다
          const overflowed = tooManyValues(values)
          if (overflowed.length) {
            // 조사를 하드코딩하면 단건일 때 `색상은(는)` 이 된다 — 계산에 맡긴다 (PR #810 5차 봇 리뷰)
            MAlert.show(`${withParticle(overflowed.join('·'), '은', '는')} 한 번에 하나만 조회할 수 있습니다. 값을 하나만 남겨 주세요.`)
            return
          }

          // 화주명을 고르면 화주유형은 조회에 쓰이지 않는다 — BE 가 shipperCode 를 우선한다.
          // 둘이 어긋난 채로 조회하면 「화면엔 MLT, 결과는 MWP」가 되므로 먼저 알린다.
          // (유형만 바꿨을 때 화주명을 비우는 것은 useShipperNameFilterOptions 가 하지만,
          //  사용자가 두 칸을 모두 의도적으로 채운 경우는 그 훅이 건드리지 않는다)
          if (params.shipperCode && params.serviceTypes?.length) {
            const info = shippers.find((sh) => sh.shipperCode === params.shipperCode)
            if (info && !params.serviceTypes.includes(info.logisticsServiceType)) {
              MAlert.show(
                `선택한 화주(${info.shipperName})는 ${info.logisticsServiceType} 입니다. ` +
                  `화주유형과 맞지 않아 조회 결과가 화주유형 선택과 다르게 나옵니다.`,
              )
              return
            }
          }
          onSearch(params)
        }}
        onReset={(form) => {
          if (baseShipperCode) form.setFieldsValue({ shipperNames: [baseShipperCode] })
          const shipperValues = scopedShipperValues(baseShipperCode)
          if (shipperValues) form.setFieldsValue(shipperValues)
          onReset()
        }}
      />
    </div>
  )
}
